import { useEffect, useState, DependencyList } from 'react';
import axios from 'axios';
import { SpaceshipApiError } from '../../utils/helpers';
import Logger from '../../utils/logger';

interface UseRequest<T> {
  data: T | undefined;
  error: SpaceshipApiError | undefined;
  loading: boolean;
  trigger: () => void;
}

interface UseRequestOptions<K> {
  params?: K;
  skip?: boolean;
}

const useRequest = <K extends unknown[], T>(
  request: (...params: K) => Promise<T>,
  options: UseRequestOptions<K> = {},
  dependency: DependencyList = [],
): UseRequest<T> => {
  let mounted = true;
  const { params = [] as unknown as K, skip } = options;
  const [data, setData] = useState<UseRequest<T>['data']>(undefined);
  const [error, setError] = useState<UseRequest<T>['error']>(undefined);
  const [loading, setLoading] = useState(false);

  const trigger = async () => {
    try {
      if (mounted) {
        setLoading(true);
      }
      const data = await request(...params);

      if (mounted) {
        setData(data);
        setLoading(false);
      }
    } catch (err: any) {
      if (axios.isCancel(err)) {
        Logger.info('Request cancelled');
      } else {
        setError(err);
        if (mounted) {
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    !skip && trigger();
  }, dependency);

  useEffect(() => {
    return () => {
      mounted = false;
    };
  }, []);

  return { data, error, loading, trigger };
};

export default useRequest;
